/**
 * Página do Serviço: Análise de Produtividade
 * WorkStream - Monitoramento inteligente de produtividade
 */
import { Link } from 'react-router-dom'
import { 
  Activity, Users, BarChart3, Brain,
  ArrowRight, Play, Eye, Target, LineChart
} from 'lucide-react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const features = [
  {
    icon: Eye,
    title: 'Análise de Postura',
    description: 'Detecta postura de trabalho e movimento. Identifica tempo ativo vs. ocioso.',
  },
  {
    icon: Activity,
    title: 'Tempo Real',
    description: 'Monitoramento contínuo com dashboards atualizados a cada segundo.',
  },
  {
    icon: BarChart3,
    title: 'Score de Produtividade',
    description: 'Score de 0-100 calculado por IA. Compare equipes e períodos.',
  },
  {
    icon: Brain,
    title: 'Insights da IA',
    description: 'Recomendações automáticas para melhorar performance e identificar gargalos.',
  },
  {
    icon: Target,
    title: 'Metas Personalizadas',
    description: 'Defina metas por equipe, turno ou colaborador. Acompanhe progresso.',
  },
  {
    icon: LineChart,
    title: 'Relatórios Detalhados',
    description: 'Exportação PDF/Excel. Agendamento automático. Comparativos temporais.',
  },
]

const stats = [
  { value: '+23%', label: 'Produtividade Média' },
  { value: '-45%', label: 'Tempo Ocioso' },
  { value: '94%', label: 'Precisão' },
  { value: '24/7', label: 'Monitoramento' },
]

const metrics = [
  { title: 'Tempo Ativo', value: '6.2h', change: '+12%', positive: true },
  { title: 'Eficiência', value: '87%', change: '+5%', positive: true },
  { title: 'Ociosidade', value: '4%', change: '-2%', positive: true },
  { title: 'Entregas', value: '95%', change: '+8%', positive: true },
]

export default function ProdutividadePage() {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-br from-blue-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
              <Activity className="w-4 h-4" />
              SERVIÇO 2 - PRODUTIVIDADE
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Fim da{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">
                improdutividade invisível.
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Visão computacional que analisa postura, movimento e atividade em tempo real.
              Descubra onde seu time pode melhorar.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/demo"
                className="px-8 py-4 rounded-xl font-semibold text-white transition-all flex items-center gap-2"
                style={{ 
                  background: 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)',
                  boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)'
                }}
              >
                Testar Grátis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="px-8 py-4 rounded-xl font-semibold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-all flex items-center gap-2">
                <Play className="w-5 h-5" />
                Ver Demo
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <p className="text-3xl font-bold text-blue-400">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Dashboard Preview */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Dashboard em Tempo Real
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Visualize a produtividade da sua equipe em tempo real
            </p>
          </div>
          
          {/* Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
            {metrics.map((metric) => (
              <div key={metric.title} className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
                <p className="text-sm text-slate-400 mb-1">{metric.title}</p>
                <p className="text-3xl font-bold text-white mb-2">{metric.value}</p>
                <span className={`text-sm ${metric.positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {metric.change} vs. semana anterior
                </span>
              </div>
            ))}
          </div>
          
          {/* Chart Placeholder */}
          <div className="max-w-4xl mx-auto p-8 rounded-2xl bg-slate-800/30 border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Produtividade por Hora</h3>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  Esta semana
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-slate-600"></span>
                  Semana anterior
                </span>
              </div>
            </div>
            <div className="h-64 flex items-end gap-2">
              {[40, 55, 70, 85, 90, 95, 88, 75, 82, 78, 65, 50].map((height, i) => (
                <div key={i} className="flex-1 flex flex-col gap-1">
                  <div 
                    className="bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t-lg transition-all hover:opacity-80"
                    style={{ height: `${height}%` }}
                  />
                  <span className="text-xs text-slate-500 text-center">{6 + i}h</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Recursos de Produtividade
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Tudo que você precisa para maximizar a performance do seu time
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon
              return (
                <div
                  key={feature.title}
                  className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-blue-500/30 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      
      {/* Privacy Note */}
      <section className="py-16 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <Users className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Privacidade Garantida</h3>
            <p className="text-slate-400">
              <strong className="text-white">LGPD Compliant.</strong> Todos os colaboradores são informados e consentem.
              O objetivo é melhoria contínua, não punição. Cada colaborador tem acesso aos seus próprios dados.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-500/10 to-cyan-600/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Descubra a produtividade real do seu time
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Comece gratuitamente por 14 dias. Sem cartão de crédito.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white transition-all"
            style={{ 
              background: 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)',
              boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)'
            }}
          >
            Começar Teste Grátis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}

